import axios from 'axios'
import CacheStorageService from './cacheStorage.service'
class SignerService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl
  }

  /**
  * @author Lucia Alvarado
  * @description Create signature Request
  * @date 29-07-2020
  * @param {String} token
  * @param {String} password
  * @param {String} signRequestId
  * @param {Boolean} newSign
  * @returns {Object}
  * @memberof SignerService
  */
  async createConfirmSign(signRequestDocumentData, token) {
    let response = await axios.post(`${this.apiUrl}/signer-confirm-sign`, signRequestDocumentData,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data
  }


  async signDocumentPackage(signRequestDocumentData, token) {
    let response = await axios.post(`${this.apiUrl}/sign-document-package`, signRequestDocumentData,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data
  }

  async signDocumentPackageOpen(signRequestDocumentData, updateSignData, signgRequestIsPackage, token) {
    let response
    let signPackageResponse = await axios.post(`${this.apiUrl}/sign-document-package-public`, signRequestDocumentData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })

    const accessToken = signPackageResponse.data.data.access_token

    response = await axios.post(`${this.apiUrl}/signer-sign`,
      signRequestDocumentData,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })


    return response.data
  }


  /**
  * @author Adrian Montemayor
  * @description Create a digital sign for a user
  * @date 04-10-2023
  * @param {Object} data
  * @param {String} token
  * @returns {Object}
  * @memberof SignerService
  */
  async updateShowSign(data, token) {
    let response = await axios.put(`${this.apiUrl}/update-inbox`, data,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data
  }


  /**
  * @author Alan Aguilar
  * @description Create a digital sign for a user
  * @date 22-10-2020
  * @param {Object} signRequestDocumentData
  * @param {String} token
  * @returns {Object}
  * @memberof SignerService
  */
  async createDigitalSignUser(signRequestDocumentData, token) {
    let response = await axios.post(`${this.apiUrl}/signer-create-pfx`, signRequestDocumentData,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data
  }

  /**
  * @author Lucia Alvarado
  * @description Reject a Sign request
  * @updatedDate 03-06-2021
  * @param {String} signRequestId
  * @param {String} signStatusId
  * @param {String} comments
  * @param {String} token
  * @returns {Object}
  * @memberof SignerService
  */
  async createRejectSign(signRequestId, signStatusId, comments, token) {
    let response = await axios.post(`${this.apiUrl}/signer-reject-sign`,
      {
        signRequestId,
        signStatusId,
        comments
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data
  }

  /**
* @author Adrian Montemayor
* @description Filter Signs
* @returns {Object}
* @memberof SignerService
*/

  async getFilterSign(filter, token) {
    let { dateFrom, dateTo, nameSign, signRequestStatusId, signerEmail, signatureTypeId, timezone, offset, limit } = filter
    let params = { dateFrom, dateTo, nameSign, signRequestStatusId, signerEmail, signatureTypeId, timezone, offset, limit }
    if (offset) params.offset = offset
    if (limit) params.limit = limit

    let response = await axios.get(`${this.apiUrl}/signers-by-filter`,
      {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data.data
  }

  /**
  * @author Edgar Escobedo
  * @description Upload identity document signer
  * @createdDate 13-08-2024
  * @param {String} signRequestId
  * @param {String} signStatusId
  * @param {String} comments
  * @param {String} token
  * @returns {Object}
  * @memberof SignerService
  */
  async uploadIdentityDocumentSigner(image, token) {
    let response = await axios.post(
      `${this.apiUrl}/upload-identity-document-signer`,
      image,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Pedro Antonio
  * @description Sign by volume
  * @createdDate 19-09-2024
  * @param {FormData} bodyData El FormData debe tener el siguiente formato:
  * ```js
  * const formData = new FormData()
  * //Debe ser de tipo Blob
  * formData.append("sign1", autographSign)
  * //Debe ser un array de UUIDs
  * formData.append("solicitudeIds", solicitudeIds)
  * //Debe ser un string con el siguiente formato: 2024-Sep-19T11:08:21-0600
  * formData.append("signatureStampTime", signatureStampTime)
  * //Debe ser un boolean para saber si es móvil o no
  * formData.append("isMobile", isMobile)
  * ```
  * @param {string} token
  * @returns {Object}
  * @memberof SignerService
  */
  async signByVolume(bodyData, token) {
    let response = await axios.post(
      `${this.apiUrl}/sign-by-volume`,
      bodyData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }


  /**
  * @author Edgar Escobedo
  * @description Create signature Request
  * @date 29-07-2020
  * @typedef {FormData} signRequestDocumentData All information to sign
  * @typedef {String} token  Token to make request
  * @returns {Object}
  * @memberof SignerService
  */
  async signerSignRequest(signRequestDocumentData, token) {
    let response = await axios.post(`${this.apiUrl}/signer-sign`, signRequestDocumentData,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

    const cache = new CacheStorageService()
    cache.deleteUrlFromCache('/binnacle')

    return response.data
  }

  /**
* @author Edgar Escobedo
* @description Signer authorizes signRequest
* @date 03-12-2024
* @typedef {FormData} authorizeData All information to authorize
* @property {String} signRequestId Sign request id
* @property {Number} signatureTypeId Sign request id
* @typedef {String} token  Token to make request  
* @returns {Object}
* @memberof SignerService
*/
  async signerAuthorizes(authorizeData, token) {
    let response = await axios.post(`${this.apiUrl}/signer-authorizes`,
      authorizeData,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    return response.data
  }
}

export { SignerService as default }
